import type { FunctionComponent, MouseEventHandler } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { StarIcon } from '@trello/nachos/icons/star';
import { StarredIcon } from '@trello/nachos/icons/starred';
import { DynamicButton } from '@trello/dynamic-tokens';

import { useBoardHeaderStarButtonQuery } from './BoardHeaderStarButtonQuery.generated';
import { useAddBoardStarMutation } from 'app/src/components/BoardTile/AddBoardStarMutation.generated';
import { useRemoveBoardStarMutation } from 'app/src/components/BoardTile/RemoveBoardStarMutation.generated';

import { Util } from 'app/scripts/lib/util';
import classNames from 'classnames';

import styles from './BoardHeaderStarButton.less';

import { forTemplate } from '@trello/i18n';
const format = forTemplate('board_header');

interface Props {
  boardId: string;
  isTemplate: boolean;
  memberId: string;
}

export const BoardHeaderStarButton: FunctionComponent<Props> = ({
  boardId,
  isTemplate,
  memberId,
}) => {
  const [pendingChange, setPendingChange] = useState<
    'starring' | 'unstarring' | 'none'
  >('none');

  const [removeBoardStar] = useRemoveBoardStarMutation();
  const [addBoardStar] = useAddBoardStarMutation();

  const {
    data: boardStarsData,
    loading: boardStarsLoading,
  } = useBoardHeaderStarButtonQuery({
    variables: { id: memberId },
    waitOn: ['MemberQuickBoards'],
    fetchPolicy: 'cache-only',
  });
  const boardStars = useMemo(() => boardStarsData?.member?.boardStars ?? [], [
    boardStarsData?.member?.boardStars,
  ]);

  const boardStar = useMemo(() => {
    return boardStars.find((boardStar) => boardStar.idBoard === boardId);
  }, [boardId, boardStars]);

  useEffect(() => {
    if (
      (pendingChange === 'starring' && boardStar) ||
      (pendingChange === 'unstarring' && !boardStar)
    ) {
      setPendingChange('none');
    }
  }, [boardStar, pendingChange]);

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (boardStar) {
        setPendingChange('unstarring');
        removeBoardStar({
          variables: {
            boardStarId: boardStar.id,
            memberId: memberId,
          },
        });
      } else {
        setPendingChange('starring');
        addBoardStar({
          variables: {
            boardId,
            memberId,
            pos:
              Math.max(0, ...boardStars.map(({ pos }) => pos)) + Util.spacing,
          },
        });
      }
    },
    [addBoardStar, boardId, boardStar, boardStars, memberId, removeBoardStar],
  );

  return (
    <DynamicButton
      className={styles.boardStarButton}
      onClick={
        // We don't want to process clicks if we have a change in progress
        // (e.g. we wouldn't know which id we were removing) or if we're still
        // loading data (we wouldn't know if the board was currently starred
        // or what position we should put a newly starred board at)
        pendingChange === 'none' && !boardStarsLoading ? onClick : undefined
      }
      title={
        isTemplate
          ? format(
              'click-to-star-or-unstar-this-template-starred-templates-show-up-at-the-top-of-your-boards-list',
            )
          : format(
              'click-to-star-or-unstar-this-board-starred-boards-show-up-at-the-top-of-your-boards-list',
            )
      }
      aria-label={format('star-or-unstar-board')}
      iconBefore={
        pendingChange === 'starring' ||
        (pendingChange === 'none' && boardStar) ? (
          <StarredIcon
            dangerous_className={classNames(styles.icon, styles.starred)}
          />
        ) : (
          <StarIcon
            dangerous_className={classNames(styles.icon, styles.unstarred)}
          />
        )
      }
    />
  );
};
